import PptxGenJS from 'pptxgenjs';
import { COLORS, FONTS, LAYOUT, SECTION_SPACING, SLIDE_BOTTOM_SAFE, TITLE_TO_BODY } from '../helpers/constants';
import { estimateTechnologyBulletsHeight, isPresent, toBulletRuns } from '../helpers';
import { PPTImageManager } from '../helpers/image-manager';


export function addProjectBadge(
    slide: PptxGenJS.Slide,
    projectCode: string,
    width: number = 2.80,
    colorFill: string = COLORS.primary
) {
    const x = LAYOUT.margin;
    const y = 0.36;
    const w = width;
    const h = 0.32;
    const badgeText = projectCode ?? 'project xxx';

    slide.addText(badgeText.toUpperCase(), {
        x, y, w, h,
        shape: 'roundRect',
        fontFace: FONTS.title,
        fontSize: 14,
        bold: true,
        color: COLORS.white,
        align: 'center',
        valign: 'middle',
        fill: { color: colorFill },
        rectRadius: 0.5,
    });
}

export function addSlideTitle(
    slide: PptxGenJS.Slide,
    slideTitle: string,
    color: string = COLORS.accent
) {
    const x = LAYOUT.margin;
    const y = 0.80;
    const w = 6.40;
    const h = 0.45;

    slide.addText(slideTitle, {
        x, y, w, h,
        fontFace: FONTS.title,
        fontSize: 28,
        bold: true,
        color,
        align: 'left',
        valign: 'middle',
    });
}

export function addConfidentialDisclaimer(
    slide: PptxGenJS.Slide,
) {

    const x = LAYOUT.margin - 0.1;
    const y = 10.30;
    const w = 3.42;
    const h = 0.20;

    slide.addText('STRICTLY PRIVATE AND CONFIDENTIAL', {
        x, y, w, h,
        fontFace: FONTS.body,
        fontSize: 11,
        bold: true,
        color: COLORS.red,
        align: 'left',
        valign: 'middle',
    });
}

export function regularSlideGradient(
    slide: PptxGenJS.Slide,
) {
    const WIDTH = LAYOUT.width;
    const HEIGHT = LAYOUT.height;

    const gradientSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH * 100}" height="${HEIGHT * 100}" viewBox="0 0 ${WIDTH * 100} ${HEIGHT * 100}">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stop-color="#FFFFFF"/>
          <stop offset="60%" stop-color="#F0FAF5"/>
          <stop offset="100%" stop-color="#DBF3E6"/>
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="url(#g)"/>
    </svg>
  `;
    const gradientBase64 = `data:image/svg+xml;base64,${btoa(gradientSvg)}`;

    slide.addImage({ data: gradientBase64, x: 0, y: 0, w: WIDTH, h: HEIGHT });
}

export function addBulletsSection(
    slide: PptxGenJS.Slide,
    title: string,
    runs: any,
    yTitle: number,
    yBody: number,
    bodyHeight: number,
    icon?: string | null
) {

    const MARGIN = LAYOUT.margin;
    const ICON_CIRCLE_SIZE = 0.35;
    const ICON_SIZE = 0.24;
    let titleX = MARGIN;

    const iconCircleX = MARGIN;
    const iconCircleY = yTitle - 0.025;

    slide.addShape('ellipse', {
        x: iconCircleX,
        y: iconCircleY,
        w: ICON_CIRCLE_SIZE,
        h: ICON_CIRCLE_SIZE,
        fill: { color: COLORS.bulletGray },
        line: { type: 'none' },
    });

    if (icon) {
        const iconOffset = (ICON_CIRCLE_SIZE - ICON_SIZE) / 2;
        slide.addImage({
            data: icon,
            x: iconCircleX + iconOffset,
            y: iconCircleY + iconOffset,
            w: ICON_SIZE,
            h: ICON_SIZE,
            sizing: { type: 'contain', w: ICON_SIZE, h: ICON_SIZE }
        });

        titleX = iconCircleX + ICON_CIRCLE_SIZE + 0.15;
    }

    slide.addText(title, {
        x: 0.85,
        y: yTitle,
        w: icon ? 6 - (titleX - MARGIN) : 6,
        h: 0.3,
        fontFace: FONTS.title,
        fontSize: 14,
        bold: true,
        align: 'left',
        valign: 'middle',
    });

    slide.addText(runs.length ? runs : [{ text: '—', options: { bullet: true } }], {
        x: MARGIN,
        y: yBody,
        w: 6.45,
        h: bodyHeight,
        fontFace: FONTS.body,
        fontSize: 12,
        align: 'left',
        valign: 'top',
    });
}

export function addTechnologySections(
    slide: PptxGenJS.Slide,
    technologies: any,
    startY: number,
    imageManager: PPTImageManager
): number {
    let yTitle = startY;

    for (const tech of technologies) {
        const cleanFields = (Array.isArray(tech.fields) ? tech.fields : [])
            .filter((field: any) => isPresent(field?.value))
            .map((field: any) => ({ label: String(field.label), value: String(field.value) }));

        if (cleanFields.length === 0) continue;

        const bullets = toBulletRuns(cleanFields);
        const h = estimateTechnologyBulletsHeight(cleanFields.length);

        if (yTitle + TITLE_TO_BODY + h > SLIDE_BOTTOM_SAFE) break;

        const yBody = yTitle + TITLE_TO_BODY;
        const icon = imageManager.get(`icon-${tech.collection}-black`)
        addBulletsSection(slide, `${tech.type} Details & Design`, bullets, yTitle, yBody, h, icon);

        yTitle = yBody + h + SECTION_SPACING;
    }

    return yTitle;
}

export function createBaseSlide(pptx: PptxGenJS, data: any, logo: string | null, slideNumber: number = 1) {
    const slide = pptx.addSlide();

    // Background gradient
    regularSlideGradient(slide);

    if (logo) {
        slide.addImage({
            data: logo,
            x: 5.26,
            y: 0.24,
            w: 1.92,
            h: 0.58,
        });
    }

    // Badge
    const badgeText = (data.projectTitle ?? 'PROJECT XXX').toUpperCase();
    addProjectBadge(slide, badgeText);

    // Title
    const titleText = slideNumber === 1 ? 'Project XXX' : `Project XXX (cont.)`;
    addSlideTitle(slide, titleText);

    // Disclaimer
    addConfidentialDisclaimer(slide);

    return slide;
}

export function contactSection(slide: PptxGenJS.Slide, imageManager: PPTImageManager, url: string) {

    const MARGIN = LAYOUT.margin;
    const icons = {
        globe: imageManager.get('globe'),
        mail: imageManager.get('mail'),
        click: imageManager.get('click')
    };

    // 1) Web
    slide.addShape('ellipse', {
        x: MARGIN,
        y: 8.48,
        w: 0.25,
        h: 0.25,
        fill: { color: COLORS.accent },
        line: { type: 'none' },
    });
    slide.addImage({
        data: icons.globe as string,
        x: 0.54,
        y: 8.52,
        w: 0.17,
        h: 0.17,
        sizing: { type: 'contain', w: 0.17, h: 0.17 }
    });

    slide.addText('www.nteaser.com', {
        x: MARGIN + 0.25,
        y: 8.46,
        w: 2,
        h: 0.25,
        fontFace: FONTS.body,
        fontSize: 12,
        color: COLORS.white,
        align: 'left',
        valign: 'middle',
        hyperlink: { url: "https://www.nteaser.com" }
    });

    // 2) Email
    slide.addShape('ellipse', {
        x: MARGIN,
        y: 8.82,
        w: 0.25,
        h: 0.25,
        fill: { color: COLORS.accent },
        line: { type: 'none' },
    });
    slide.addImage({
        data: icons.mail as string,
        x: 0.54,
        y: 8.86,
        w: 0.17,
        h: 0.17,
        sizing: { type: 'contain', w: 0.17, h: 0.17 }
    });

    slide.addText('projects@nteaser.com', {
        x: MARGIN + 0.25,
        y: 8.8,
        w: 2,
        h: 0.25,
        fontFace: FONTS.body,
        fontSize: 12,
        color: COLORS.white,
        align: 'left',
        valign: 'middle',
        hyperlink: { url: "mailto:projects@nteaser.com" }
    });

    // 3) Link text
    slide.addShape('ellipse', {
        x: MARGIN,
        y: 9.16,
        w: 0.25,
        h: 0.25,
        fill: { color: COLORS.accent },
        line: { type: 'none' },
    });
    slide.addImage({
        data: icons.click as string,
        x: 0.54,
        y: 9.20,
        w: 0.17,
        h: 0.17,
        sizing: { type: 'contain', w: 0.17, h: 0.17 }
    });

    slide.addText('Click for more details', {
        x: MARGIN + 0.25,
        y: 9.16,
        w: 2,
        h: 0.25,
        fontFace: FONTS.body,
        fontSize: 12,
        bold: true,
        color: COLORS.accent,
        align: 'left',
        valign: 'middle',
        hyperlink: { url }
    });
}
